import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";

const links = [
  { to: "/leistungen", label: "Leistungen" },
  { to: "/preise", label: "Preise" },
  { to: "/blog", label: "Blog" },
  { to: "/faq", label: "FAQ" },
  { to: "/ueber-mich", label: "Über mich" },
  { to: "/kontakt", label: "Kontakt" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Logo click: if already on home → smooth scroll to top, else navigate home
  const handleLogoClick = (e) => {
    if (pathname === "/") {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      // Let react-router handle navigation; Layout will reset scroll to top.
      navigate("/");
    }
  };

  const linkClass = ({ isActive }) =>
    `text-sm transition-colors ${
      isActive ? "text-zinc-900 font-medium" : "text-zinc-600 hover:text-zinc-900"
    }`;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/70 backdrop-blur-2xl border-b border-zinc-200/60 saturate-150"
          : "bg-white/40 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
        <Link
          to="/"
          onClick={handleLogoClick}
          data-testid="nav-logo"
          className="font-display text-xl font-semibold tracking-tight text-zinc-900 cursor-pointer hover:opacity-80 transition-opacity"
          aria-label="Zur Startseite"
        >
          NovaSite<span className="text-zinc-400">.</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={`nav-${l.to.slice(1)}`} className={linkClass}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/kontakt"
            data-testid="nav-cta-projekt-starten"
            className="hidden md:inline-flex items-center justify-center rounded-full bg-zinc-900 text-white text-sm font-medium px-5 py-2.5 hover:bg-zinc-800 transition-all hover:scale-[1.03]"
          >
            Projekt starten
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            aria-label="Menü"
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-zinc-200"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-zinc-100"
          data-testid="mobile-menu"
        >
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)} className="text-base text-zinc-800">
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/kontakt"
              onClick={() => setOpen(false)}
              className="inline-flex items-center justify-center rounded-full bg-zinc-900 text-white text-sm font-medium px-5 py-3"
            >
              Projekt starten
            </Link>
          </div>
        </motion.div>
      )}
    </header>
  );
}
